import { DAYS_IN_WEEK } from '@new/constants';
import type { UserAvailabilitySlot } from '@teachur/http-client';
import {
  LOCALE,
  HOUR_INCREMENT,
  DAY_INCREMENT,
  FIRST_HOUR,
  LAST_HOUR,
  SUNDAY,
  DAYS_BEFORE_SUNDAY,
  MONDAY,
  LAST_DAY_OF_WEEK,
  END_OF_DAY_HOUR,
  END_OF_DAY_MINUTE,
  END_OF_DAY_SECOND,
  END_OF_DAY_MILLISECOND,
  ZERO_MINUTES,
  ZERO_SECONDS,
  ZERO_MILLISECONDS,
  PAD_LENGTH,
  PAD_CHAR,
  TIME_MINUTES_ZERO,
} from './constants';

export interface TimeSlot {
  day: number;
  hour: number;
  date: Date;
}

export interface SelectedSlotRange {
  start: TimeSlot;
  end: TimeSlot;
}

// Shared helpers

export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const formatDateShort = (date: Date): string => {
  return date.toLocaleDateString(LOCALE, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateWithYear = (date: Date): string => {
  return date.toLocaleDateString(LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Calendar helpers

export const getMonthBoundaries = (
  year: number,
  month: number
): { monthStart: Date; monthEnd: Date } => {
  const FIRST_DAY_OF_MONTH = 1;
  const NEXT_MONTH_OFFSET = 1;
  const LAST_DAY_OF_PREVIOUS_MONTH = 0;

  const monthStart = new Date(year, month, FIRST_DAY_OF_MONTH);
  monthStart.setHours(ZERO_MINUTES, ZERO_MINUTES, ZERO_SECONDS, ZERO_MILLISECONDS);

  const monthEnd = new Date(year, month + NEXT_MONTH_OFFSET, LAST_DAY_OF_PREVIOUS_MONTH);
  monthEnd.setHours(END_OF_DAY_HOUR, END_OF_DAY_MINUTE, END_OF_DAY_SECOND, END_OF_DAY_MILLISECOND);

  return { monthStart, monthEnd };
};

export const generateCalendarDays = (
  year: number,
  month: number
): Array<{ date: Date | null; dayNumber: number | null }> => {
  const result: Array<{ date: Date | null; dayNumber: number | null }> = [];

  // Calculate days in month (day 0 of next month = last day of current month)
  const NEXT_MONTH_OFFSET = 1;
  const LAST_DAY_OF_PREVIOUS_MONTH = 0;
  const daysInMonth = new Date(
    year,
    month + NEXT_MONTH_OFFSET,
    LAST_DAY_OF_PREVIOUS_MONTH
  ).getDate();

  // Calculate first day of week (0 = Sunday, 6 = Saturday)
  // Convert to Monday-based (0 = Monday, 6 = Sunday)
  const FIRST_DAY_OF_MONTH = 1;
  const DAYS_IN_WEEK = 7;
  const SUNDAY_TO_MONDAY_OFFSET = 6;
  const firstDayOfWeek =
    (new Date(year, month, FIRST_DAY_OF_MONTH).getDay() + SUNDAY_TO_MONDAY_OFFSET) % DAYS_IN_WEEK;

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfWeek; i++) {
    result.push({ date: null, dayNumber: null });
  }

  // Add cells for each day of the month
  for (let day = 1; day <= daysInMonth; day++) {
    result.push({ date: new Date(year, month, day), dayNumber: day });
  }

  return result;
};

// Utility functions

export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const getTimezoneOffset = (): string => {
  const timeString = new Date().toLocaleTimeString('en-US', {
    timeZoneName: 'short',
  });
  return timeString.split(' ')[2] || '';
};

export const formatHour = (hour: number): string => {
  return `${hour.toString().padStart(PAD_LENGTH, PAD_CHAR)}:${TIME_MINUTES_ZERO}`;
};

export const formatDateNoYear = (date: Date): string => {
  return date.toLocaleDateString(LOCALE, {
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateLong = (date: Date): string => {
  return date.toLocaleDateString(LOCALE, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatMonthYear = (date: Date): string => {
  return date.toLocaleDateString(LOCALE, {
    month: 'long',
    year: 'numeric',
  });
};

export const formatSlotTime = (start: string, end: string): string => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const dateStr = formatDateShort(startDate);
  const startTime = formatTime(startDate);
  const endTime = formatTime(endDate);

  return `${dateStr} • ${startTime} - ${endTime}`;
};

export const getStartOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === SUNDAY ? DAYS_BEFORE_SUNDAY : MONDAY);
  const result = new Date(d.setDate(diff));
  result.setHours(ZERO_MINUTES, ZERO_MINUTES, ZERO_SECONDS, ZERO_MILLISECONDS);
  return result;
};

export const getEndOfWeek = (date: Date): Date => {
  const start = getStartOfWeek(date);
  const end = new Date(start);
  end.setDate(start.getDate() + LAST_DAY_OF_WEEK);
  end.setHours(END_OF_DAY_HOUR, END_OF_DAY_MINUTE, END_OF_DAY_SECOND, END_OF_DAY_MILLISECOND);
  return end;
};

export const getWeekDates = (startDate: Date): Date[] => {
  const dates: Date[] = [];
  for (let i = 0; i < DAYS_IN_WEEK; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    dates.push(date);
  }
  return dates;
};

export const isSameSlot = (slot1: TimeSlot, slot2: TimeSlot): boolean => {
  return slot1.day === slot2.day && slot1.hour === slot2.hour;
};

export const isSlotInRange = (slot: TimeSlot, start: TimeSlot, end: TimeSlot): boolean => {
  if (start.day === end.day) {
    return slot.day === start.day && slot.hour >= start.hour && slot.hour <= end.hour;
  }

  if (slot.day === start.day) {
    return slot.hour >= start.hour;
  }

  if (slot.day === end.day) {
    return slot.hour <= end.hour;
  }

  return slot.day > start.day && slot.day < end.day;
};

export const createDateFromSlot = (weekStart: Date, slot: TimeSlot): Date => {
  const date = new Date(weekStart);
  date.setDate(weekStart.getDate() + slot.day);
  date.setHours(slot.hour, ZERO_MINUTES, ZERO_SECONDS, ZERO_MILLISECONDS);
  return date;
};

export const isSlotBooked = (
  slot: TimeSlot,
  weekStart: Date,
  availabilitySlots: UserAvailabilitySlot[]
): UserAvailabilitySlot | undefined => {
  const slotDate = createDateFromSlot(weekStart, slot);
  const slotEnd = new Date(slotDate);
  slotEnd.setHours(slotDate.getHours() + HOUR_INCREMENT);

  return availabilitySlots.find((availSlot) => {
    const start = new Date(availSlot.start);
    const end = new Date(availSlot.end);
    return slotDate >= start && slotDate < end;
  });
};

export type SlotStatus = 'available' | 'pending' | 'accepted' | 'rejected';

export const getSlotStatus = (slot: UserAvailabilitySlot | undefined): SlotStatus => {
  if (!slot?.scheduledCallId) {
    return 'available';
  }

  const status = slot.scheduledCall?.status;
  if (status === 'accepted' || status === 'rejected' || status === 'pending') {
    return status;
  }

  return 'pending';
};

export const isSlotPast = (slot: TimeSlot, weekStart: Date): boolean => {
  const slotDate = createDateFromSlot(weekStart, slot);
  return slotDate < new Date();
};

export const formatDateRange = (start: Date, end: Date): string => {
  const startStr = formatDateNoYear(start);
  const endStr = formatDateWithYear(end);
  return `${startStr} - ${endStr}`;
};

export const getSlotsInRange = (start: TimeSlot, end: TimeSlot): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  const weekStart = new Date();

  if (start.day === end.day) {
    for (let hour = start.hour; hour <= end.hour; hour++) {
      slots.push({
        day: start.day,
        hour,
        date: createDateFromSlot(weekStart, { day: start.day, hour, date: weekStart }),
      });
    }
  } else {
    for (let day = start.day; day <= end.day; day++) {
      const startHour = day === start.day ? start.hour : FIRST_HOUR;
      const endHour = day === end.day ? end.hour : LAST_HOUR;

      for (let hour = startHour; hour <= endHour; hour++) {
        slots.push({
          day,
          hour,
          date: createDateFromSlot(weekStart, { day, hour, date: weekStart }),
        });
      }
    }
  }

  return slots;
};

export const generateBulkSlots = (
  startDate: Date,
  endDate: Date,
  startTime: string,
  endTime: string,
  selectedDays: number[]
): Array<{ start: string; end: string }> => {
  const slots: Array<{ start: string; end: string }> = [];
  const [startHour] = startTime.split(':').map(Number);
  const [endHour] = endTime.split(':').map(Number);

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + DAY_INCREMENT)) {
    const dayOfWeek = d.getDay();

    if (selectedDays.includes(dayOfWeek)) {
      for (let hour = startHour; hour < endHour; hour++) {
        const hourStart = new Date(d);
        hourStart.setHours(hour, ZERO_MINUTES, ZERO_SECONDS, ZERO_MILLISECONDS);

        const hourEnd = new Date(d);
        hourEnd.setHours(hour + HOUR_INCREMENT, ZERO_MINUTES, ZERO_SECONDS, ZERO_MILLISECONDS);

        slots.push({
          start: hourStart.toISOString(),
          end: hourEnd.toISOString(),
        });
      }
    }
  }

  return slots;
};

export const addBusinessHours = (from: Date, hours: number): Date => {
  const SATURDAY = 6;
  const result = new Date(from);
  let remaining = hours;

  while (remaining > 0) {
    result.setHours(result.getHours() + 1);
    const day = result.getDay();
    if (day !== SUNDAY && day !== SATURDAY) {
      remaining--;
    }
  }

  return result;
};

export const formatSelectedDayTitle = (date: Date): string => {
  return date.toLocaleDateString(LOCALE, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
};

export const formatSlotStartTime = (start: string): string => {
  return new Date(start).toLocaleTimeString(LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};
